import { useState } from "react";
import type { Book } from "@shared/types";
import { SafeCover } from "./SafeCover";

/**
 * Minimal confirmation form for a scanned book. Shows the auto-populated
 * fields, lets the user tweak title/author/notes, and submits.
 *
 * NEVER render any field as raw HTML. React's default escaping handles
 * external API data (title, description, etc.) correctly; don't bypass it.
 */
export function BookConfirm({
  book,
  onSave,
  onCancel,
}: {
  book: Book;
  onSave: (book: Book) => void | Promise<void>;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState(book.title);
  const [authors, setAuthors] = useState(book.authors.join(", "));
  const [notes, setNotes] = useState(book.notes ?? "");
  const [readingStatus, setReadingStatus] = useState(book.reading_status);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    try {
      await onSave({
        ...book,
        uuid: book.uuid || crypto.randomUUID(),
        title: title.trim(),
        authors: authors
          .split(",")
          .map((s) => s.trim())
          .filter((s) => s.length > 0),
        notes: notes.trim() || null,
        reading_status: readingStatus,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-3">
        <SafeCover url={book.cover_url} alt={book.title} className="w-20 h-28" />
        <div className="flex-1 text-sm text-gray-400 space-y-1">
          {book.publisher && <div>{book.publisher}</div>}
          {book.publish_date && <div>{book.publish_date}</div>}
          {book.isbn_13 && <div className="font-mono text-xs">ISBN {book.isbn_13}</div>}
        </div>
      </div>

      <Field label="Title">
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className={inputCls}
        />
      </Field>
      <Field label="Authors (comma separated)">
        <input
          type="text"
          value={authors}
          onChange={(e) => setAuthors(e.target.value)}
          className={inputCls}
        />
      </Field>
      <Field label="Reading status">
        <select
          value={readingStatus}
          onChange={(e) => setReadingStatus(e.target.value)}
          className={inputCls}
        >
          <option value="unread">Unread</option>
          <option value="reading">Currently reading</option>
          <option value="read">Read</option>
        </select>
      </Field>
      <Field label="Notes">
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          className={inputCls}
        />
      </Field>

      <div className="flex gap-2 pt-2">
        <button
          onClick={submit}
          disabled={submitting || !title.trim()}
          className="flex-1 py-2 bg-blue-600 hover:bg-blue-500 disabled:bg-blue-800 disabled:text-gray-300 rounded font-medium"
        >
          {submitting ? "Saving…" : "Save"}
        </button>
        <button
          onClick={onCancel}
          className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1">
      <span className="text-sm text-gray-300">{label}</span>
      {children}
    </label>
  );
}

const inputCls =
  "w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-500";
